<template>
    <div class="sp-comm-composer" data-community-composer>
        <div
            class="sp-comm-composer__box"
            :class="{
                'is-focused': focused,
                'is-disabled': disabled,
                'has-attachments': attachments.length,
            }"
        >
            <textarea
                :id="inputId"
                ref="composerInput"
                :value="modelValue"
                rows="3"
                :placeholder="placeholder"
                :disabled="disabled"
                :maxlength="maxLength"
                aria-label="Start your message"
                class="sp-comm-composer__input"
                @input="onInput"
                @keydown="onKeydown"
                @focus="focused = true"
                @blur="focused = false"
            ></textarea>
            <div v-if="attachments.length" class="sp-comm-composer__chips">
                <div
                    v-for="(item, index) in attachments"
                    :key="`${item.kind}-${item.name || item.emoji}-${index}`"
                    class="sp-comm-composer__chip"
                    :class="`is-${item.kind || 'file'}`"
                >
                    <img
                        v-if="(item.kind === 'image' || item.kind === 'gif') && previewUrl(item)"
                        :src="previewUrl(item)"
                        alt=""
                        class="sp-comm-composer__thumb"
                    >
                    <span v-else-if="item.kind === 'sticker'" class="sp-comm-composer__sticker">{{ item.emoji }}</span>
                    <span v-else class="sp-comm-composer__file-mark" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <path d="M14 2v6h6" />
                        </svg>
                    </span>
                    <span class="sp-comm-composer__chip-label">{{ chipLabel(item) }}</span>
                    <button
                        type="button"
                        aria-label="Remove attachment"
                        class="sp-comm-composer__chip-remove"
                        :disabled="disabled"
                        @click="removeAttachment(index)"
                    >✕</button>
                </div>
            </div>
            <p v-if="attachError" class="sp-comm-composer__error">{{ attachError }}</p>
            <div class="sp-comm-composer__bar">
                <button
                    ref="plusBtn"
                    type="button"
                    title="Message tools"
                    aria-label="Message tools"
                    :aria-expanded="plusMenu ? 'true' : 'false'"
                    :disabled="disabled || uploading"
                    class="sp-comm-composer__tool"
                    :class="{ 'is-open': plusMenu }"
                    @click="togglePlusMenu"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                </button>
                <p v-if="uploading" class="sp-comm-composer__hint">Uploading file…</p>
                <p v-else-if="!canSend && !disabled" class="sp-comm-composer__hint">Pick a star rating to send</p>
                <div class="sp-comm-composer__grow"></div>
                <button
                    type="button"
                    title="Send message"
                    aria-label="Send message"
                    :disabled="!canSend || disabled || uploading"
                    class="sp-comm-composer__send"
                    :class="{ 'is-ready': canSend && !disabled && !uploading }"
                    @click="emitSend"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M22 2 11 13M22 2l-7 20-4-9-9-4Z" />
                    </svg>
                </button>
            </div>
        </div>

        <input
            ref="imageInput"
            type="file"
            accept="image/*"
            class="sp-comm-composer__file"
            @change="onPickImage"
        >
        <input
            ref="fileInput"
            type="file"
            accept="*/*"
            class="sp-comm-composer__file"
            @change="onPickFile"
        >

        <Teleport to="body">
            <div
                v-if="plusMenu"
                ref="plusMenuPop"
                :style="popStyle"
            >
                <div class="sp-comm-composer__menu">
                    <p class="sp-comm-composer__menu-kicker">Attach to feedback</p>
                    <button
                        v-for="tool in plusTools"
                        :key="tool.label"
                        type="button"
                        class="sp-comm-composer__menu-item"
                        @click="onPlusMenuTool(tool)"
                    >
                        <span class="sp-comm-composer__menu-icon" v-html="menuIcon(tool.icon)"></span>
                        {{ tool.label }}
                    </button>
                </div>
            </div>
            <div
                v-if="showEmoji"
                ref="emojiPop"
                :style="popStyle"
            >
                <div class="sp-comm-composer__picker">
                    <div class="sp-comm-composer__picker-head">
                        <span>EMOJI</span>
                        <button type="button" aria-label="Close emoji picker" @click="showEmoji = false">✕</button>
                    </div>
                    <div v-for="group in emojiSet" :key="group.cat">
                        <div class="sp-comm-composer__picker-cat">{{ group.cat.toUpperCase() }}</div>
                        <div class="sp-comm-composer__emoji-row">
                            <button
                                v-for="em in group.list"
                                :key="`${group.cat}-${em}`"
                                type="button"
                                class="sp-comm-composer__emoji"
                                @click="insertEmoji(em)"
                            >{{ em }}</button>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="showSticker"
                ref="stickerPop"
                :style="popStyle"
            >
                <div class="sp-comm-composer__picker">
                    <div class="sp-comm-composer__picker-head">
                        <span>STICKERS</span>
                        <button type="button" aria-label="Close sticker picker" @click="showSticker = false">✕</button>
                    </div>
                    <div class="sp-comm-composer__sticker-grid">
                        <button
                            v-for="em in stickers"
                            :key="`sticker-${em}`"
                            type="button"
                            class="sp-comm-composer__sticker-pick"
                            @click="pickSticker(em)"
                        >{{ em }}</button>
                    </div>
                    <p class="sp-comm-composer__note">Sends as a sticker with your feedback.</p>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import {
    FEATURE_INTEREST_MAX_FILE_BYTES,
    FEATURE_INTEREST_MAX_FILES,
    uploadFeatureInterestFile,
} from '@/components/Instructor/StatePortal/statePortalAnalytics.js';

const COMPOSER_MAX_H = 160;
const PLUS_TOOLS = [
    { icon: 'emoji', label: 'Emoji' },
    { icon: 'gif', label: 'GIF' },
    { icon: 'camera', label: 'Image' },
    { icon: 'folder', label: 'Upload File' },
];
const EMOJI_SET = [
    { cat: 'Smileys', list: ['😀', '😁', '😂', '🤣', '😊', '😍', '🤩', '😎', '🤔', '🤨', '😐', '😴', '🥳', '😭', '😤', '😅', '🙃', '😇', '🤯', '🥶'] },
    { cat: 'Gestures', list: ['👍', '👎', '👏', '🙌', '🤝', '✊', '👊', '🤞', '✌️', '🤙', '👋', '🫡', '💪', '🙏', '👌', '🖐️'] },
    { cat: 'Hearts & Hype', list: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💯', '💥', '✨', '🔥', '⭐', '🎉', '🎊'] },
    { cat: 'Sports', list: ['🏀', '🏈', '⚽', '⚾', '🥎', '🎾', '🏐', '🏉', '🥍', '🏒', '🏑', '🏓', '⛳', '🥊', '🏆', '🥇', '🥈', '🥉', '📣', '🏁'] },
    { cat: 'Work', list: ['💬', '📌', '📎', '✅', '❌', '⚠️', '❗', '❓', '💡', '📝', '📅', '⏰', '🔔', '🚀', '🎯', '🧠'] },
];
const STICKERS = ['🎉', '🔥', '👏', '💯', '🏆', '😂', '😮', '🥳', '❤️', '👍', '🚀', '🤯', '😎', '🙏', '💪', '⚽', '🏀', '🏈', '🥇', '📣', '😴', '☕', '🎯', '🫡'];
const MENU_ICON_PATHS = {
    emoji: '<circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />',
    gif: '<circle cx="10.5" cy="10.5" r="7" /><path d="M21 21l-5-5" /><path d="M9 8l4 2.5L9 13z" fill="currentColor" stroke="none" />',
    camera: '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />',
    folder: '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />',
};

let composerSeq = 0;

export default {
    name: 'StatePortalCommunityComposer',
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Start your message',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        canSend: {
            type: Boolean,
            default: false,
        },
        sending: {
            type: Boolean,
            default: false,
        },
        maxLength: {
            type: Number,
            default: 2000,
        },
        inputId: {
            type: String,
            default: '',
        },
        layerZ: {
            type: Number,
            default: 200220,
        },
    },
    emits: ['update:modelValue', 'update:attachments', 'send'],
    data() {
        composerSeq += 1;
        return {
            uid: composerSeq,
            plusMenu: false,
            showEmoji: false,
            showSticker: false,
            focused: false,
            uploading: false,
            attachments: [],
            attachError: '',
            popStyle: {
                position: 'fixed',
                left: '-9999px',
                top: '-9999px',
                opacity: 0,
                zIndex: 200220,
                maxHeight: 'calc(100vh - 16px)',
                overflowY: 'auto',
            },
            plusTools: PLUS_TOOLS,
            emojiSet: EMOJI_SET,
            stickers: STICKERS,
        };
    },
    watch: {
        plusMenu() {
            this.$nextTick(() => this.placePop());
        },
        showEmoji() {
            this.$nextTick(() => this.placePop());
        },
        showSticker() {
            this.$nextTick(() => this.placePop());
        },
        attachments: {
            deep: true,
            handler(next) {
                this.$emit('update:attachments', next);
            },
        },
    },
    mounted() {
        this._onDocMouseDown = (event) => this.onDocMouseDown(event);
        this._onResize = () => this.placePop();
        document.addEventListener('mousedown', this._onDocMouseDown);
        window.addEventListener('resize', this._onResize);
        this.$nextTick(() => this.autoGrow());
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this._onDocMouseDown);
        window.removeEventListener('resize', this._onResize);
    },
    methods: {
        onInput(event) {
            this.$emit('update:modelValue', event.target.value);
            this.autoGrow();
        },
        autoGrow() {
            const el = this.$refs.composerInput;
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_H)}px`;
        },
        onKeydown(event) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.emitSend();
            }
            if (event.key === 'Escape') this.closePops();
        },
        emitSend() {
            if (!this.canSend || this.disabled || this.uploading) return;
            this.closePops();
            this.$emit('send', {
                text: String(this.modelValue || ''),
                attachments: this.attachments.map((item) => ({ ...item })),
            });
        },
        togglePlusMenu() {
            if (this.disabled || this.uploading) return;
            this.plusMenu = !this.plusMenu;
            this.showEmoji = false;
            this.showSticker = false;
            this.attachError = '';
        },
        closePops() {
            this.plusMenu = false;
            this.showEmoji = false;
            this.showSticker = false;
        },
        onPlusMenuTool(tool = {}) {
            const label = String(tool?.label || '').trim();
            this.plusMenu = false;
            if (label === 'Emoji') {
                this.showEmoji = true;
                this.showSticker = false;
                return;
            }
            if (label === 'GIF') {
                this.showSticker = true;
                this.showEmoji = false;
                return;
            }
            if (label === 'Image') {
                this.$refs.imageInput?.click();
                return;
            }
            if (label === 'Upload File') {
                this.$refs.fileInput?.click();
            }
        },
        insertEmoji(em = '') {
            const next = String(em || '');
            if (!next) return;
            const el = this.$refs.composerInput;
            const current = String(this.modelValue || '');
            const caret = el && Number.isFinite(el.selectionStart) ? el.selectionStart : current.length;
            const value = `${current.slice(0, caret)}${next}${current.slice(caret)}`.slice(0, this.maxLength);
            this.$emit('update:modelValue', value);
            this.showEmoji = false;
            this.$nextTick(() => {
                if (el) {
                    const pos = Math.min(caret + next.length, value.length);
                    el.focus();
                    el.setSelectionRange(pos, pos);
                }
                this.autoGrow();
            });
        },
        pickSticker(em = '') {
            const next = String(em || '').trim();
            if (!next) return;
            this.addAttachment({
                kind: 'sticker',
                name: 'sticker',
                emoji: next,
                mime: 'sticker',
                size: 0,
            });
            this.showSticker = false;
        },
        async onPickImage(event) {
            const file = event?.target?.files?.[0];
            event.target.value = '';
            await this.uploadPickedFile(file);
        },
        async onPickFile(event) {
            const file = event?.target?.files?.[0];
            event.target.value = '';
            await this.uploadPickedFile(file);
        },
        async uploadPickedFile(file) {
            if (!file) return;
            this.attachError = '';
            if (this.attachments.length >= FEATURE_INTEREST_MAX_FILES) {
                this.attachError = `You can attach up to ${FEATURE_INTEREST_MAX_FILES} items.`;
                return;
            }
            if (file.size > FEATURE_INTEREST_MAX_FILE_BYTES) {
                this.attachError = `Each file must be ${FEATURE_INTEREST_MAX_FILE_BYTES / (1024 * 1024)} MB or smaller`;
                return;
            }
            this.uploading = true;
            const result = await uploadFeatureInterestFile(file);
            this.uploading = false;
            if (!result?.uploaded || !result?.attachment) {
                this.attachError = result?.message || 'Couldn’t upload that file.';
                return;
            }
            this.addAttachment(result.attachment);
        },
        addAttachment(item) {
            if (this.attachments.length >= FEATURE_INTEREST_MAX_FILES) {
                this.attachError = `You can attach up to ${FEATURE_INTEREST_MAX_FILES} items.`;
                return;
            }
            this.attachments = [...this.attachments, item];
        },
        previewUrl(item = {}) {
            return String(item?.fileUrl || item?.dataUrl || '').trim();
        },
        removeAttachment(index) {
            this.attachments = this.attachments.filter((_, i) => i !== index);
            this.attachError = '';
        },
        chipLabel(item = {}) {
            if (item.kind === 'sticker') return 'sticker';
            if (item.kind === 'image') return item.name || 'image';
            return item.name || 'file';
        },
        menuIcon(name = '') {
            const inner = MENU_ICON_PATHS[name];
            if (!inner) return '';
            return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;color:#00cdc2">${inner}</svg>`;
        },
        placePop() {
            const open = this.plusMenu || this.showEmoji || this.showSticker;
            const anchor = this.$refs.plusBtn;
            const pop = this.plusMenu
                ? this.$refs.plusMenuPop
                : (this.showEmoji ? this.$refs.emojiPop : this.$refs.stickerPop);
            if (!open || !anchor || !pop) return;
            const a = anchor.getBoundingClientRect();
            const b = pop.getBoundingClientRect();
            const pad = 8;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const left = Math.max(pad, Math.min(a.right + 6, vw - (b.width || 190) - pad));
            const top = Math.max(pad, Math.min(a.bottom - (b.height || 160), vh - (b.height || 160) - pad));
            this.popStyle = {
                position: 'fixed',
                left: `${left}px`,
                top: `${top}px`,
                opacity: 1,
                zIndex: this.layerZ,
                maxHeight: 'calc(100vh - 16px)',
                overflowY: 'auto',
            };
        },
        onDocMouseDown(event) {
            if (!this.plusMenu && !this.showEmoji && !this.showSticker) return;
            const target = event.target;
            const nodes = [
                this.$refs.plusBtn,
                this.$refs.plusMenuPop,
                this.$refs.emojiPop,
                this.$refs.stickerPop,
            ];
            if (nodes.some((node) => node && node.contains && node.contains(target))) return;
            this.closePops();
        },
        reset() {
            this.attachments = [];
            this.attachError = '';
            this.uploading = false;
            this.closePops();
            this.$nextTick(() => this.autoGrow());
        },
    },
};
</script>

<style scoped>
.sp-comm-composer {
    width: 100%;
    text-align: left;
}
.sp-comm-composer__box {
    position: relative;
    min-width: 0;
    overflow: hidden;
    background: #fff;
    border: 1px solid #dbe4ee;
    border-radius: 1rem;
    box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.8) inset,
        0 8px 24px -16px rgba(15, 23, 42, 0.28);
    transition: border-color 160ms ease, box-shadow 160ms ease;
}
.sp-comm-composer__box.is-focused {
    border-color: #5eead4;
    box-shadow:
        0 0 0 3px rgba(13, 148, 136, 0.14),
        0 10px 24px -16px rgba(13, 148, 136, 0.35);
}
.sp-comm-composer__box.is-disabled {
    background: #f8fafc;
    opacity: 0.72;
}
.sp-comm-composer__input {
    display: block;
    width: 100%;
    min-height: 4.75rem;
    max-height: 10rem;
    resize: none;
    border: 0;
    outline: none;
    background: transparent;
    padding: 0.9rem 1rem 0.45rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #0f172a;
    font-family: inherit;
}
.sp-comm-composer__input::placeholder {
    color: #94a3b8;
}
.sp-comm-composer__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    padding: 0 0.85rem 0.55rem;
}
.sp-comm-composer__chip {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    max-width: 100%;
    min-width: 0;
    background: #f0fdfa;
    border: 1px solid #ccfbf1;
    border-radius: 999px;
    padding: 0.28rem 0.4rem 0.28rem 0.35rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #0f766e;
}
.sp-comm-composer__chip.is-file {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #334155;
}
.sp-comm-composer__thumb {
    height: 1.4rem;
    width: 1.4rem;
    border-radius: 999px;
    object-fit: cover;
}
.sp-comm-composer__sticker,
.sp-comm-composer__file-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 999px;
    background: #fff;
    font-size: 0.95rem;
    line-height: 1;
}
.sp-comm-composer__chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    max-width: 10rem;
}
.sp-comm-composer__chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.15rem;
    height: 1.15rem;
    margin-left: 0.05rem;
    border: 0;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.05);
    color: #64748b;
    cursor: pointer;
    font-size: 0.7rem;
    line-height: 1;
}
.sp-comm-composer__chip-remove:hover {
    background: #fee2e2;
    color: #b91c1c;
}
.sp-comm-composer__error {
    margin: 0 0.9rem 0.35rem;
    font-size: 0.72rem;
    color: #b45309;
}
.sp-comm-composer__bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.55rem 0.55rem;
    border-top: 1px solid #eef2f6;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}
.sp-comm-composer__hint {
    margin: 0;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: #94a3b8;
}
.sp-comm-composer__grow {
    flex: 1;
}
.sp-comm-composer__tool,
.sp-comm-composer__send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    border-radius: 999px;
    border: 1px solid #d9e2ec;
    background: #fff;
    color: #0d9488;
    cursor: pointer;
    transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 120ms ease, box-shadow 150ms ease;
}
.sp-comm-composer__tool:hover:not(:disabled),
.sp-comm-composer__send:hover:not(:disabled) {
    border-color: #99f6e4;
    background: #f0fdfa;
    transform: translateY(-1px);
}
.sp-comm-composer__tool.is-open {
    border-color: #14b8a6;
    background: #ccfbf1;
    color: #0f766e;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.12);
}
.sp-comm-composer__send.is-ready {
    border-color: transparent;
    background: linear-gradient(180deg, #14b8a6 0%, #0d9488 100%);
    color: #fff;
    box-shadow: 0 8px 16px -10px rgba(13, 148, 136, 0.9);
}
.sp-comm-composer__send:disabled,
.sp-comm-composer__tool:disabled {
    cursor: default;
    opacity: 0.45;
    transform: none;
}
.sp-comm-composer__file {
    display: none;
}
.sp-comm-composer__menu {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.9rem;
    padding: 0.45rem;
    min-width: 12.5rem;
    box-shadow: 0 18px 40px -22px rgba(15, 23, 42, 0.45);
    display: flex;
    flex-direction: column;
}
.sp-comm-composer__menu-kicker {
    margin: 0.15rem 0.55rem 0.35rem;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #94a3b8;
}
.sp-comm-composer__menu-item {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 0.55rem 0.6rem;
    cursor: pointer;
    color: #0f172a;
    font-size: 0.8125rem;
    font-weight: 600;
    font-family: inherit;
    border-radius: 0.65rem;
}
.sp-comm-composer__menu-item:hover {
    background: #f0fdfa;
    color: #0f766e;
}
.sp-comm-composer__menu-icon {
    width: 1.5rem;
    height: 1.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background: #f0fdfa;
}
.sp-comm-composer__picker {
    width: 18.5rem;
    max-width: calc(100vw - 24px);
    max-height: 16.5rem;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    padding: 0.75rem;
    box-shadow: 0 18px 40px -22px rgba(15, 23, 42, 0.45);
}
.sp-comm-composer__picker-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.35rem;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    color: #94a3b8;
}
.sp-comm-composer__picker-head button {
    background: none;
    border: 0;
    color: #94a3b8;
    cursor: pointer;
    font-size: 0.85rem;
}
.sp-comm-composer__picker-cat {
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #94a3b8;
    margin: 0.55rem 0 0.3rem;
}
.sp-comm-composer__emoji-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.15rem;
}
.sp-comm-composer__emoji {
    background: none;
    border: 0;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0.2rem;
    border-radius: 0.45rem;
}
.sp-comm-composer__emoji:hover,
.sp-comm-composer__sticker-pick:hover {
    background: #f0fdfa;
}
.sp-comm-composer__sticker-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 0.35rem;
}
.sp-comm-composer__sticker-pick {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.65rem;
    font-size: 1.35rem;
    cursor: pointer;
    padding: 0.4rem 0;
}
.sp-comm-composer__note {
    font-size: 0.7rem;
    color: #94a3b8;
    margin: 0.6rem 0 0;
    line-height: 1.45;
}
</style>
